import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../api";
import {
  MessagesSquare, Loader2, Send, Star, RotateCcw, Flag, History, ChevronRight,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

const TYPES = [
  { key: "technical", label: "Technical" },
  { key: "behavioral", label: "Behavioral" },
  { key: "hr", label: "HR Round" },
];

function Stars({ n }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={13} className={i <= n ? "text-amber-500 fill-amber-500" : "text-rule"} />
      ))}
    </span>
  );
}

function FeedbackReport({ fb, onRetry }) {
  const ratings = fb.ratings || {};
  return (
    <div className="card card-pad" data-testid="prep-feedback">
      <span className="eyebrow">Interview report</span>
      <div className="mt-3 flex items-center gap-5 flex-wrap">
        <div className="border border-rule rounded-xl px-5 py-3 text-center">
          <div className="font-display font-black text-4xl text-signal" data-testid="prep-overall-score">{fb.overall_score}</div>
          <div className="text-[11px] text-muted uppercase tracking-wide">Overall</div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {[["communication", "Communication"], ["technical_depth", "Technical depth"],
            ["structure", "Structure"], ["confidence", "Confidence"]].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted">{l}</span><Stars n={ratings[k] || 0} />
            </div>
          ))}
        </div>
      </div>
      {fb.verdict && <p className="text-sm italic text-muted mt-4 border-l-2 border-signal pl-3">{fb.verdict}</p>}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="text-xs font-semibold text-green-700 mb-1">What went well</div>
          <ul className="space-y-1">{(fb.strengths || []).map(s => <li key={s} className="text-sm text-muted flex gap-2"><span className="text-green-600">•</span>{s}</li>)}</ul>
        </div>
        <div>
          <div className="text-xs font-semibold text-amber-700 mb-1">Work on next</div>
          <ul className="space-y-1">{(fb.improvements || []).map(s => <li key={s} className="text-sm text-muted flex gap-2"><span className="text-amber-600">•</span>{s}</li>)}</ul>
        </div>
      </div>
      <button className="btn-primary mt-5" onClick={onRetry} data-testid="prep-retry-btn">
        <RotateCcw size={14} /> Retry this interview
      </button>
    </div>
  );
}

function SessionHistory({ onOpen, refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/prep/mock/sessions").then(r => setRows(r.data)).catch(() => {});
  }, [refreshKey]);
  if (rows.length === 0) return null;
  return (
    <div className="card card-pad" data-testid="prep-history">
      <span className="eyebrow inline-flex items-center gap-1"><History size={12} /> Past interviews</span>
      <div className="mt-3 space-y-2">
        {rows.slice(0, 8).map(s => (
          <button key={s.id} onClick={() => onOpen(s)}
                  className="w-full flex items-center justify-between gap-3 text-left border border-rule rounded-xl px-3 py-2 hover:border-signal transition"
                  data-testid={`prep-session-${s.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{s.target_role}</div>
              <div className="text-[11px] text-muted capitalize">{s.interview_type} · {new Date(s.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {s.feedback?.overall_score != null
                ? <span className="font-bold text-signal text-sm">{s.feedback.overall_score}</span>
                : <span className="badge">{s.status}</span>}
              <ChevronRight size={14} className="text-muted" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StudentPrep() {
  usePageTitle("Interview Preparation");
  const [role, setRole] = useState("");
  const [type, setType] = useState("technical");
  const [session, setSession] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [err, setErr] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const start = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(""); setFeedback(null);
    try {
      const r = await api.post("/prep/mock/start", { target_role: role, interview_type: type });
      setSession(r.data);
      setMsgs([{ role: "assistant", content: r.data.question }]);
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs(m => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true); setErr("");
    try {
      const r = await api.post(`/prep/mock/${session.session_id}/answer`, { answer: text });
      setMsgs(m => [...m, { role: "assistant", content: r.data.reply }]);
      setSession(s => ({ ...s, questions_asked: r.data.questions_asked, can_finish: r.data.can_finish }));
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    setFinishing(true); setErr("");
    try {
      const r = await api.post(`/prep/mock/${session.session_id}/finish`);
      setFeedback(r.data.feedback);
      setSession(null); setMsgs([]);
    } catch (e2) { setErr(formatError(e2)); }
    finally { setFinishing(false); }
  };

  const openHistory = async (s) => {
    if (s.feedback) { setFeedback(s.feedback); setSession(null); setMsgs([]); setRole(s.target_role); setType(s.interview_type); }
  };

  return (
    <div className="space-y-6" data-testid="prep-page">
      {!session && (
        <div className="card card-pad">
          <span className="eyebrow inline-flex items-center gap-1"><MessagesSquare size={12} className="text-signal" /> AI mock interview</span>
          <h2 className="font-display font-bold text-2xl mt-1">Practice with an AI interviewer</h2>
          <p className="text-muted text-sm mt-1">6 questions · instant per-answer feedback · full report at the end. Retry as many times as you like.</p>
          <form onSubmit={start} className="mt-4 space-y-3">
            <div className="flex gap-2 flex-wrap">
              {TYPES.map(t => (
                <button key={t.key} type="button" onClick={() => setType(t.key)}
                        className={`px-4 py-2 rounded-xl border text-sm font-semibold transition ${type === t.key ? "border-signal bg-signal/5 text-signal" : "border-rule text-muted hover:border-signal/40"}`}
                        data-testid={`prep-type-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              <input className="input flex-1 min-w-[220px]" placeholder="Target role — e.g. Data Engineer"
                     value={role} onChange={e => setRole(e.target.value)} data-testid="prep-role-input" />
              <button className="btn-primary" disabled={busy || role.trim().length < 3} data-testid="prep-start-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <MessagesSquare size={14} />}
                {busy ? "Starting…" : "Start interview"}
              </button>
            </div>
          </form>
          {err && <div className="text-xs text-red-600 mt-3" data-testid="prep-err">{err}</div>}
        </div>
      )}

      {session && (
        <div className="card overflow-hidden" data-testid="prep-chat">
          <div className="px-5 py-3 border-b border-rule flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-display font-bold text-sm">{session.target_role}</div>
              <div className="text-[11px] text-muted capitalize">{type} interview · Question {session.questions_asked}/{session.max_questions}</div>
            </div>
            <button className="btn-secondary" onClick={finish} disabled={finishing || !session.can_finish} data-testid="prep-finish-btn">
              {finishing ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />}
              {finishing ? "Scoring…" : "Finish & get report"}
            </button>
          </div>
          <div className="p-5 space-y-3 max-h-[480px] overflow-y-auto bg-paper/40">
            {msgs.map((m, i) => (
              <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-signal text-white" : "bg-white border border-rule"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-muted flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Interviewer is typing…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-rule flex gap-2">
            <textarea className="input flex-1 min-h-[44px] max-h-[140px]" rows={2}
                      placeholder="Type your answer… (Shift+Enter for newline)"
                      value={input} onChange={e => setInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      data-testid="prep-answer-input" />
            <button className="btn-primary self-end" disabled={busy || !input.trim()} data-testid="prep-send-btn">
              <Send size={14} />
            </button>
          </form>
          {err && <div className="text-xs text-red-600 px-4 pb-3" data-testid="prep-chat-err">{err}</div>}
        </div>
      )}

      {feedback && <FeedbackReport fb={feedback} onRetry={() => { setFeedback(null); start(); }} />}

      {!session && <SessionHistory onOpen={openHistory} refreshKey={feedback} />}
    </div>
  );
}
